import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { CalendarDays, Dog, User, CheckCircle, Percent, Plus, AlertCircle } from 'lucide-react';
import { dogBoardingAddOns } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pricing constants
const PRICING = {
  'dog-boarding': { base: 60, label: 'Dog Boarding' },
  'day-boarding': { base: 40, label: 'Day Boarding' },
  'private-custom': { base: 80, label: 'Tailored Care' }
};

const Booking = () => {
  const [step, setStep] = useState(1);
  const [checkInDate, setCheckInDate] = useState();
  const [checkOutDate, setCheckOutDate] = useState();
  const [checkInTime, setCheckInTime] = useState('');
  const [checkOutTime, setCheckOutTime] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [maxCapacity, setMaxCapacity] = useState(6);
  const [capacityInfo, setCapacityInfo] = useState({});
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState('');

  // Valid promo codes
  const VALID_PROMO_CODES = {
    'SADIE26': { discount: 0.10, label: '10% off' }
  };

  // Apply promo code
  const applyPromoCode = () => {
    const code = promoCode.toUpperCase().trim();
    if (VALID_PROMO_CODES[code]) {
      setPromoApplied(true);
      setPromoError('');
    } else {
      setPromoApplied(false);
      setPromoError('Invalid promo code');
    }
  };

  // Remove promo code
  const removePromoCode = () => {
    setPromoCode('');
    setPromoApplied(false);
    setPromoError('');
  };

  // Fetch unavailable dates on mount and when dates change
  useEffect(() => {
    const fetchUnavailableDates = async () => {
      try {
        // Get dates for next 12 months
        const startDate = new Date();
        const endDate = new Date();
        endDate.setMonth(endDate.getMonth() + 12);
        
        const response = await axios.get(`${API}/bookings/capacity/unavailable-dates`, {
          params: {
            start_date: startDate.toISOString().split('T')[0],
            end_date: endDate.toISOString().split('T')[0]
          }
        });
        
        // Convert date strings to Date objects for the calendar
        const unavailable = response.data.unavailableDates.map(dateStr => new Date(dateStr + 'T00:00:00'));
        setUnavailableDates(unavailable);
        setMaxCapacity(response.data.maxCapacity);
        setCapacityInfo(response.data.capacityInfo);
      } catch (error) {
        console.error('Error fetching unavailable dates:', error);
      }
    };
    
    fetchUnavailableDates();
  }, []);

  // Check if a date is unavailable
  const isDateUnavailable = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return unavailableDates.some(unavailDate => 
      unavailDate.toISOString().split('T')[0] === dateStr
    );
  };

  // Owner Information
  const [ownerInfo, setOwnerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    emergencyContact: '',
    emergencyPhone: ''
  });

  // Dog Information
  const [dogInfo, setDogInfo] = useState({
    dogName: '',
    breed: '',
    age: '',
    gender: '',
    weight: '',
    serviceType: '',
    specialNeeds: '',
    medications: '',
    veterinarian: '',
    vetPhone: '',
    isVaccinated: false,
    hasAggression: false,
    additionalNotes: '',
    // Photo upload
    photoUrl: '',
    photoFile: null,
    // Spayed/Neutered
    isSpayedNeutered: '',
    // Vaccines (MANDATORY)
    rabiesDate: '',
    rabiesBrand: '',
    dhppDate: '',
    dhppBrand: '',
    bordetellaDate: '',
    // Check-in/Check-out times
    checkInTime: '',
    checkOutTime: '',
    // Eating habits
    foodAmount: '',
    mealsPerDay: '',
    feedingSchedule: '',
    foodBrand: '',
    foodType: '',
    dietaryRestrictions: '',
    // Behavior information
    behaviorWithDogs: '',
    greetingPreference: '',
    playgroundBehavior: '',
    humanInteraction: ''
  });

  // Calculate number of nights
  const numberOfNights = useMemo(() => {
    if (!checkInDate || !checkOutDate) return 0;
    return Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));
  }, [checkInDate, checkOutDate]);

  // Calculate pricing with automatic 10% discount for 8+ nights and promo code
  const pricingDetails = useMemo(() => {
    const serviceType = dogInfo.serviceType || 'dog-boarding';
    const pricing = PRICING[serviceType] || PRICING['dog-boarding'];
    const baseRate = pricing.base;
    const isDayBoarding = serviceType === 'day-boarding';
    // Day boarding: minimum 1 day (same-day booking counts as 1 day)
    const stayCount = isDayBoarding ? Math.max(numberOfNights, 1) : numberOfNights;
    const unitLabel = isDayBoarding ? 'day' : 'night';
    
    const subtotal = baseRate * stayCount;
    const qualifiesForDiscount = stayCount >= 8;
    const stayDiscountAmount = qualifiesForDiscount ? subtotal * 0.10 : 0;
    const boardingAfterStayDiscount = subtotal - stayDiscountAmount;
    
    // Calculate add-ons total
    const addOnsTotal = selectedAddOns.reduce((total, addOn) => {
      const priceNum = parseFloat(addOn.price.replace('$', ''));
      // If add-on has "per 24 hours", multiply by stay count
      if (addOn.unit === 'per 24 hours') {
        return total + (priceNum * stayCount);
      }
      return total + priceNum;
    }, 0);
    
    // Late pickup charge
    const latePickupCharge = checkOutTime === 'weekday-4-5' ? 40 : 0;
    
    // Subtotal before promo (boarding + addons + late fee)
    const subtotalBeforePromo = boardingAfterStayDiscount + addOnsTotal + latePickupCharge;
    
    // Promo code discount (applies to total before tax)
    const promoDiscount = promoApplied ? subtotalBeforePromo * 0.10 : 0;
    
    const grandTotal = subtotalBeforePromo - promoDiscount;
    
    return {
      serviceLabel: pricing.label,
      baseRate,
      stayCount,
      unitLabel,
      isDayBoarding,
      subtotal,
      qualifiesForDiscount,
      stayDiscountAmount,
      boardingAfterStayDiscount,
      addOnsTotal,
      latePickupCharge,
      subtotalBeforePromo,
      promoApplied,
      promoDiscount,
      grandTotal
    };
  }, [dogInfo.serviceType, numberOfNights, selectedAddOns, checkOutTime, promoApplied]);

  // Toggle add-on selection
  const toggleAddOn = (addOn) => {
    setSelectedAddOns(prev => {
      const isSelected = prev.some(a => a.name === addOn.name);
      if (isSelected) {
        return prev.filter(a => a.name !== addOn.name);
      } else {
        return [...prev, addOn];
      }
    });
  };

  const handleOwnerChange = (e) => {
    setOwnerInfo({ ...ownerInfo, [e.target.name]: e.target.value });
  };

  const handleDogChange = (e) => {
    setDogInfo({ ...dogInfo, [e.target.name]: e.target.value });
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create a preview URL
      const photoUrl = URL.createObjectURL(file);
      setDogInfo({ ...dogInfo, photoFile: file, photoUrl });
    }
  };

  const handleServiceTypeChange = (value) => {
    setDogInfo({ ...dogInfo, serviceType: value });
  };

  const handleCheckboxChange = (field, checked) => {
    setDogInfo({ ...dogInfo, [field]: checked });
  };

  const handleNextStep = () => {
    if (step === 1) {
      if (!checkInDate || !checkOutDate) {
        toast.error('Please select check-in and check-out dates');
        return;
      }
      if (!checkInTime || !checkOutTime) {
        toast.error('Please select check-in and check-out times');
        return;
      }
    } else if (step === 2) {
      if (!ownerInfo.firstName || !ownerInfo.lastName || !ownerInfo.email || !ownerInfo.phone) {
        toast.error('Please fill in all required owner information');
        return;
      }
    } else if (step === 3) {
      if (!dogInfo.dogName || !dogInfo.breed || !dogInfo.serviceType || !dogInfo.photoFile) {
        toast.error('Please fill in all required dog information and upload a photo');
        return;
      }
      // Validate mandatory vaccine information
      if (!dogInfo.rabiesDate || !dogInfo.rabiesBrand || !dogInfo.dhppDate || !dogInfo.dhppBrand || !dogInfo.bordetellaDate) {
        toast.error('Please provide all required vaccine information (Rabies, DHPP, Bordetella)');
        return;
      }
      // Validate spayed/neutered
      if (!dogInfo.isSpayedNeutered) {
        toast.error('Please indicate if your dog is spayed/neutered');
        return;
      }
    }
    setStep(step + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const bookingData = {
        checkInDate: checkInDate.toISOString().split('T')[0],
        checkOutDate: checkOutDate.toISOString().split('T')[0],
        checkInTime,
        checkOutTime,
        ownerInfo,
        dogInfo,
        selectedAddOns: selectedAddOns.map(a => ({ name: a.name, price: a.price, unit: a.unit })),
        pricingDetails: {
          numberOfNights,
          stayCount: pricingDetails.stayCount,
          baseRate: pricingDetails.baseRate,
          subtotal: pricingDetails.subtotal,
          discountApplied: pricingDetails.qualifiesForDiscount,
          stayDiscountAmount: pricingDetails.stayDiscountAmount,
          promoApplied: pricingDetails.promoApplied,
          promoCode: promoApplied ? promoCode.toUpperCase().trim() : null,
          promoDiscount: pricingDetails.promoDiscount,
          addOnsTotal: pricingDetails.addOnsTotal,
          latePickupCharge: pricingDetails.latePickupCharge,
          estimatedTotal: pricingDetails.grandTotal
        }
      };

      // Submit booking
      const bookingResponse = await axios.post(`${API}/bookings`, bookingData);
      
      // Submit dog profile
      await axios.post(`${API}/dog-profiles`, {
        dogInfo,
        ownerId: bookingResponse.data.id
      });

      toast.success(`Booking confirmed! Confirmation #${bookingResponse.data.confirmationNumber}. We will contact you shortly.`);
      
      // Reset form
      setStep(1);
      setCheckInDate(undefined);
      setCheckOutDate(undefined);
      setCheckInTime('');
      setCheckOutTime('');
      setSelectedAddOns([]);
      setOwnerInfo({
        firstName: '', lastName: '', email: '', phone: '',
        address: '', emergencyContact: '', emergencyPhone: ''
      });
      setDogInfo({
        dogName: '', breed: '', age: '', gender: '', weight: '',
        serviceType: '', specialNeeds: '', medications: '',
        veterinarian: '', vetPhone: '', isVaccinated: false,
        hasAggression: false, additionalNotes: ''
      });
      setPromoCode('');
      setPromoApplied(false);
      setPromoError('');
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { number: 1, title: 'Select Dates', icon: CalendarDays },
    { number: 2, title: 'Owner Info', icon: User },
    { number: 3, title: 'Dog Profile', icon: Dog },
    { number: 4, title: 'Review', icon: CheckCircle }
  ];

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex justify-between items-center">
            {steps.map((s, index) => (
              <div key={s.number} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition-colors ${
                    step >= s.number ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'
                  }`}>
                    <s.icon className="h-6 w-6" />
                  </div>
                  <span className={`mt-2 text-sm font-medium ${
                    step >= s.number ? 'text-primary' : 'text-muted-foreground'
                  }`}>
                    {s.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`h-1 flex-1 mx-2 transition-colors ${
                    step > s.number ? 'bg-primary' : 'bg-secondary'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Select Dates */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Select Your Dates & Times</CardTitle>
              <CardDescription>Choose check-in and check-out dates and time slots for your dog's stay</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-base font-semibold mb-3 block">Check-In Date</Label>
                  <Calendar
                    mode="single"
                    selected={checkInDate}
                    onSelect={setCheckInDate}
                    disabled={(date) => date < new Date() || isDateUnavailable(date)}
                    className="rounded-md border"
                    modifiers={{
                      unavailable: unavailableDates
                    }}
                    modifiersStyles={{
                      unavailable: { 
                        textDecoration: 'line-through',
                        color: '#ef4444',
                        opacity: 0.5
                      }
                    }}
                  />
                </div>
                <div>
                  <Label className="text-base font-semibold mb-3 block">Check-Out Date</Label>
                  <Calendar
                    mode="single"
                    selected={checkOutDate}
                    onSelect={setCheckOutDate}
                    disabled={(date) => date < (checkInDate || new Date()) || isDateUnavailable(date)}
                    className="rounded-md border"
                    modifiers={{
                      unavailable: unavailableDates
                    }}
                    modifiersStyles={{
                      unavailable: { 
                        textDecoration: 'line-through',
                        color: '#ef4444',
                        opacity: 0.5
                      }
                    }}
                  />
                </div>
              </div>
              
              {/* Capacity notice */}
              {unavailableDates.length > 0 && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> Dates shown with strikethrough are fully booked (maximum {maxCapacity} dogs per day).
                  </p>
                </div>
              )}
              
              {checkInDate && checkOutDate && (
                <div className="bg-secondary/50 p-4 rounded-lg space-y-2">
                  <p className="text-sm text-muted-foreground">
                    <strong>Selected Dates:</strong> {checkInDate.toLocaleDateString()} - {checkOutDate.toLocaleDateString()}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <strong>Total Nights:</strong> {numberOfNights}
                  </p>
                  {numberOfNights >= 8 && (
                    <div className="flex items-center gap-2 mt-2 p-2 bg-green-100 rounded-md">
                      <Percent className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700">
                        🎉 You qualify for 10% extended stay discount!
                      </span>
                    </div>
                  )}
                  {numberOfNights > 0 && numberOfNights < 8 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      💡 Tip: Stay 8+ nights and receive 10% off your booking!
                    </p>
                  )}
                </div>
              )}

              {/* Time Selection */}
              <div className="border-t pt-6 space-y-4">
                <h3 className="text-lg font-semibold">Drop-off & Pick-up Times</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="checkInTime">Check-in Time *</Label>
                  <Select value={checkInTime} onValueChange={setCheckInTime} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select check-in time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekday-645-745">Weekday: 6:45am - 7:45am</SelectItem>
                      <SelectItem value="weekday-845-10">Weekday: 8:45am - 10:00am</SelectItem>
                      <SelectItem value="weekend-830-930">Weekend: 8:30am - 9:30am</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="checkOutTime">Check-out Time *</Label>
                  <Select value={checkOutTime} onValueChange={setCheckOutTime} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select check-out time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekday-845-1030">Weekday: 8:45am - 10:30am</SelectItem>
                      <SelectItem value="weekday-4-5">Weekday: 4:00pm - 5:00pm (+$40 daycare charge)</SelectItem>
                      <SelectItem value="weekend-4-5">Weekend: 4:00pm - 5:00pm</SelectItem>
                      <SelectItem value="dayboarding-4-5">4:00pm - 5:00pm (Day Boarding Only)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Note: Late pick-up on weekday afternoons includes a $40 daycare charge
                  </p>
                </div>
              </div>

              {/* Additional Services Add-ons */}
              <div className="border-t pt-6 space-y-4">
                <div className="flex items-center gap-2">
                  <Plus className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-semibold">Additional Services (Optional)</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Enhance your dog's stay with these add-on services
                </p>
                <p className="text-xs text-muted-foreground italic">
                  * All services are one-time/per service unless otherwise noted
                </p>
                
                <div className="grid md:grid-cols-2 gap-3">
                  {dogBoardingAddOns.map((addOn, index) => {
                    const isSelected = selectedAddOns.some(a => a.name === addOn.name);
                    return (
                      <div
                        key={index}
                        onClick={() => toggleAddOn(addOn)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          isSelected 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Checkbox 
                                checked={isSelected}
                                className="pointer-events-none"
                              />
                              <span className="font-medium text-sm">{addOn.name}</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 ml-6">
                              {addOn.description}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="font-bold text-primary">{addOn.price}</span>
                            {addOn.unit && (
                              <span className="text-xs text-muted-foreground block">
                                {addOn.unit}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {selectedAddOns.length > 0 && (
                  <div className="bg-primary/5 p-3 rounded-lg">
                    <p className="text-sm font-medium">
                      Selected add-ons: {selectedAddOns.length}
                    </p>
                    <ul className="text-xs text-muted-foreground mt-1">
                      {selectedAddOns.map((addOn, idx) => (
                        <li key={idx}>• {addOn.name} - {addOn.price}{addOn.unit ? ` ${addOn.unit}` : ''}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <Button onClick={handleNextStep} className="w-full" size="lg">
                Continue to Owner Information
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Owner Information */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Owner Information</CardTitle>
              <CardDescription>Please provide your contact details</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={ownerInfo.firstName}
                      onChange={handleOwnerChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={ownerInfo.lastName}
                      onChange={handleOwnerChange}
                      required
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={ownerInfo.email}
                      onChange={handleOwnerChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={ownerInfo.phone}
                      onChange={handleOwnerChange}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    name="address"
                    value={ownerInfo.address}
                    onChange={handleOwnerChange}
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="emergencyContact">Emergency Contact Name</Label>
                    <Input
                      id="emergencyContact"
                      name="emergencyContact"
                      value={ownerInfo.emergencyContact}
                      onChange={handleOwnerChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emergencyPhone">Emergency Contact Phone</Label>
                    <Input
                      id="emergencyPhone"
                      name="emergencyPhone"
                      type="tel"
                      value={ownerInfo.emergencyPhone}
                      onChange={handleOwnerChange}
                    />
                  </div>
                </div>
                <div className="flex gap-4 pt-4">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">
                    Back
                  </Button>
                  <Button type="button" onClick={handleNextStep} className="flex-1">
                    Continue to Dog Profile
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Dog Profile */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Dog Profile</CardTitle>
              <CardDescription>Tell us about your furry friend</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-6">
                {/* Photo Upload - REQUIRED */}
                <div className="space-y-2">
                  <Label htmlFor="dogPhoto" className="text-base font-semibold">Dog Photo *</Label>
                  <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                    {dogInfo.photoUrl ? (
                      <div className="space-y-4">
                        <img
                          src={dogInfo.photoUrl}
                          alt="Dog preview"
                          className="mx-auto h-48 w-48 object-cover rounded-lg"
                        />
                        <p className="text-sm text-muted-foreground">Photo uploaded successfully!</p>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => document.getElementById('dogPhoto').click()}
                        >
                          Change Photo
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <Dog className="h-12 w-12 mx-auto text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">Upload your dog's photo</p>
                          <p className="text-xs text-muted-foreground">PNG, JPG up to 10MB</p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => document.getElementById('dogPhoto').click()}
                        >
                          Choose Photo
                        </Button>
                      </div>
                    )}
                    <input
                      id="dogPhoto"
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      required
                    />
                  </div>
                </div>

                {/* Basic Information */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="dogName">Dog's Name *</Label>
                      <Input
                        id="dogName"
                        name="dogName"
                        value={dogInfo.dogName}
                        onChange={handleDogChange}
                        required
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="breed">Breed *</Label>
                        <Input
                          id="breed"
                          name="breed"
                          value={dogInfo.breed}
                          onChange={handleDogChange}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="age">Age (years)</Label>
                        <Input
                          id="age"
                          name="age"
                          type="number"
                          value={dogInfo.age}
                          onChange={handleDogChange}
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="gender">Gender</Label>
                        <Select 
                          value={dogInfo.gender} 
                          onValueChange={(value) => setDogInfo({...dogInfo, gender: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="weight">Weight (lbs)</Label>
                        <Input
                          id="weight"
                          name="weight"
                          type="number"
                          value={dogInfo.weight}
                          onChange={handleDogChange}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="serviceType">Service Type *</Label>
                      <Select value={dogInfo.serviceType} onValueChange={handleServiceTypeChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dog-boarding">Dog Boarding</SelectItem>
                          <SelectItem value="day-boarding">Day Boarding</SelectItem>
                          <SelectItem value="private-custom">Private Custom Boarding</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Mandatory Vaccines */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4">Vaccination Records *</h3>
                  <p className="text-sm text-muted-foreground mb-4">All fields are mandatory for the safety of our guests</p>
                  <div className="space-y-6">
                    {/* Rabies Vaccine */}
                    <div className="space-y-3 p-4 border rounded-lg">
                      <h4 className="font-medium">1. Rabies Vaccine</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="rabiesDate">Date of Vaccination *</Label>
                          <Input
                            id="rabiesDate"
                            name="rabiesDate"
                            type="date"
                            value={dogInfo.rabiesDate}
                            onChange={handleDogChange}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="rabiesBrand">Vaccine Brand *</Label>
                          <Input
                            id="rabiesBrand"
                            name="rabiesBrand"
                            value={dogInfo.rabiesBrand}
                            onChange={handleDogChange}
                            placeholder="e.g., Nobivac, Rabvac"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* DHPP Vaccine */}
                    <div className="space-y-3 p-4 border rounded-lg">
                      <h4 className="font-medium">2. DHPP (Distemper, Hepatitis, Parainfluenza, Parvovirus)</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="dhppDate">Date of Vaccination *</Label>
                          <Input
                            id="dhppDate"
                            name="dhppDate"
                            type="date"
                            value={dogInfo.dhppDate}
                            onChange={handleDogChange}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="dhppBrand">Vaccine Brand *</Label>
                          <Input
                            id="dhppBrand"
                            name="dhppBrand"
                            value={dogInfo.dhppBrand}
                            onChange={handleDogChange}
                            placeholder="e.g., Vanguard, Nobivac"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Bordetella Vaccine */}
                    <div className="space-y-3 p-4 border rounded-lg">
                      <h4 className="font-medium">3. Bordetella (Kennel Cough)</h4>
                      <div className="space-y-2">
                        <Label htmlFor="bordetellaDate">Date of Vaccination *</Label>
                        <Input
                          id="bordetellaDate"
                          name="bordetellaDate"
                          type="date"
                          value={dogInfo.bordetellaDate}
                          onChange={handleDogChange}
                          required
                          className="md:w-1/2"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Spayed/Neutered */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4">Spay/Neuter Status *</h3>
                  <div className="space-y-2">
                    <Label htmlFor="isSpayedNeutered">Is your dog spayed or neutered? *</Label>
                    <Select value={dogInfo.isSpayedNeutered} onValueChange={(value) => setDogInfo({...dogInfo, isSpayedNeutered: value})} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes, spayed/neutered</SelectItem>
                        <SelectItem value="no">No, not spayed/neutered</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Eating Habits & Routine */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4">Eating Habits & Routine</h3>
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="foodAmount">Food Amount per Meal</Label>
                        <Input
                          id="foodAmount"
                          name="foodAmount"
                          value={dogInfo.foodAmount}
                          onChange={handleDogChange}
                          placeholder="e.g., 2 cups, 200g"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="mealsPerDay">Meals per Day</Label>
                        <Select value={dogInfo.mealsPerDay} onValueChange={(value) => setDogInfo({...dogInfo, mealsPerDay: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select meals per day" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1 meal</SelectItem>
                            <SelectItem value="2">2 meals</SelectItem>
                            <SelectItem value="3">3 meals</SelectItem>
                            <SelectItem value="4">4+ meals</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="feedingSchedule">Feeding Schedule</Label>
                      <Input
                        id="feedingSchedule"
                        name="feedingSchedule"
                        value={dogInfo.feedingSchedule}
                        onChange={handleDogChange}
                        placeholder="e.g., 8am and 6pm"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="foodBrand">Food Brand</Label>
                        <Input
                          id="foodBrand"
                          name="foodBrand"
                          value={dogInfo.foodBrand}
                          onChange={handleDogChange}
                          placeholder="e.g., Blue Buffalo, Royal Canin"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="foodType">Food Type</Label>
                        <Select value={dogInfo.foodType} onValueChange={(value) => setDogInfo({...dogInfo, foodType: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select food type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="dry">Dry Kibble</SelectItem>
                            <SelectItem value="wet">Wet/Canned</SelectItem>
                            <SelectItem value="raw">Raw Diet</SelectItem>
                            <SelectItem value="mixed">Mixed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dietaryRestrictions">Dietary Restrictions / Allergies</Label>
                      <Textarea
                        id="dietaryRestrictions"
                        name="dietaryRestrictions"
                        value={dogInfo.dietaryRestrictions}
                        onChange={handleDogChange}
                        rows={2}
                        placeholder="Any food allergies or dietary restrictions..."
                      />
                    </div>
                  </div>
                </div>

                {/* Behavior Information */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4">Behavior & Socialization</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="behaviorWithDogs">Behavior Around New Dogs</Label>
                      <Textarea
                        id="behaviorWithDogs"
                        name="behaviorWithDogs"
                        value={dogInfo.behaviorWithDogs}
                        onChange={handleDogChange}
                        rows={3}
                        placeholder="How does your dog typically behave around other dogs? (friendly, shy, playful, protective, etc.)"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="greetingPreference">Greeting & Introduction Preference</Label>
                      <Textarea
                        id="greetingPreference"
                        name="greetingPreference"
                        value={dogInfo.greetingPreference}
                        onChange={handleDogChange}
                        rows={3}
                        placeholder="How should we introduce your dog to new dogs? Any specific approach needed? (e.g., slow introduction, needs space, energetic greeter)"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="playgroundBehavior">Playground & Play Behavior</Label>
                      <Textarea
                        id="playgroundBehavior"
                        name="playgroundBehavior"
                        value={dogInfo.playgroundBehavior}
                        onChange={handleDogChange}
                        rows={3}
                        placeholder="How does your dog behave in group play? Activity level, play style, any concerns?"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="humanInteraction">Interaction with Humans</Label>
                      <Textarea
                        id="humanInteraction"
                        name="humanInteraction"
                        value={dogInfo.humanInteraction}
                        onChange={handleDogChange}
                        rows={3}
                        placeholder="How does your dog interact with new people? (friendly, cautious, needs time to warm up, etc.)"
                      />
                    </div>
                  </div>
                </div>

                {/* Medical & Special Needs */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4">Medical & Special Needs</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="specialNeeds">Special Needs / Care Requirements</Label>
                      <Textarea
                        id="specialNeeds"
                        name="specialNeeds"
                        value={dogInfo.specialNeeds}
                        onChange={handleDogChange}
                        rows={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="medications">Current Medications</Label>
                      <Textarea
                        id="medications"
                        name="medications"
                        value={dogInfo.medications}
                        onChange={handleDogChange}
                        rows={2}
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="veterinarian">Veterinarian Name</Label>
                        <Input
                          id="veterinarian"
                          name="veterinarian"
                          value={dogInfo.veterinarian}
                          onChange={handleDogChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="vetPhone">Veterinarian Phone</Label>
                        <Input
                          id="vetPhone"
                          name="vetPhone"
                          type="tel"
                          value={dogInfo.vetPhone}
                          onChange={handleDogChange}
                        />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="hasAggression"
                          checked={dogInfo.hasAggression}
                          onCheckedChange={(checked) => handleCheckboxChange('hasAggression', checked)}
                        />
                        <Label htmlFor="hasAggression" className="cursor-pointer">
                          My dog has shown aggression towards other dogs or people
                        </Label>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="additionalNotes">Additional Notes</Label>
                      <Textarea
                        id="additionalNotes"
                        name="additionalNotes"
                        value={dogInfo.additionalNotes}
                        onChange={handleDogChange}
                        rows={3}
                        placeholder="Any other information we should know..."
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button type="button" variant="outline" onClick={() => setStep(2)} className="flex-1">
                    Back
                  </Button>
                  <Button type="button" onClick={handleNextStep} className="flex-1">
                    Review Booking
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Review & Submit */}
        {step === 4 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Review Your Booking</CardTitle>
              <CardDescription>Please review all information before submitting</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Dates */}
              <div className="bg-secondary/50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">Booking Dates & Times</h3>
                <p className="text-sm text-muted-foreground">
                  Check-In: {checkInDate?.toLocaleDateString()} at {checkInTime === 'weekday-645-745' ? '6:45am - 7:45am (Weekday)' : checkInTime === 'weekday-845-10' ? '8:45am - 10:00am (Weekday)' : '8:30am - 9:30am (Weekend)'}
                </p>
                <p className="text-sm text-muted-foreground">
                  Check-Out: {checkOutDate?.toLocaleDateString()} at {checkOutTime === 'weekday-845-1030' ? '8:45am - 10:30am (Weekday)' : checkOutTime === 'weekday-4-5' ? '4:00pm - 5:00pm (Weekday +$40)' : checkOutTime === 'weekend-4-5' ? '4:00pm - 5:00pm (Weekend)' : checkOutTime === 'dayboarding-4-5' ? '4:00pm - 5:00pm (Day Boarding)' : checkOutTime}
                </p>
                <p className="text-sm font-medium mt-2">Total Nights: {numberOfNights}</p>
              </div>

              {/* Owner Info */}
              <div className="bg-secondary/50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">Owner Information</h3>
                <p className="text-sm text-muted-foreground">{ownerInfo.firstName} {ownerInfo.lastName}</p>
                <p className="text-sm text-muted-foreground">{ownerInfo.email}</p>
                <p className="text-sm text-muted-foreground">{ownerInfo.phone}</p>
              </div>

              {/* Dog Info */}
              <div className="bg-secondary/50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">Dog Information</h3>
                {dogInfo.photoUrl && (
                  <img src={dogInfo.photoUrl} alt="Dog" className="w-24 h-24 object-cover rounded-lg mb-3" />
                )}
                <p className="text-sm text-muted-foreground">Name: {dogInfo.dogName}</p>
                <p className="text-sm text-muted-foreground">Breed: {dogInfo.breed}</p>
                <p className="text-sm text-muted-foreground">Service: {pricingDetails.serviceLabel}</p>
                {dogInfo.mealsPerDay && (
                  <p className="text-sm text-muted-foreground">Meals: {dogInfo.mealsPerDay} per day</p>
                )}
              </div>

              {/* Pricing Breakdown */}
              <div className="bg-primary/5 p-4 rounded-lg border-2 border-primary/20">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  Estimated Price Breakdown
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{pricingDetails.serviceLabel} (${pricingDetails.baseRate}/{pricingDetails.unitLabel} × {pricingDetails.stayCount} {pricingDetails.unitLabel}{pricingDetails.stayCount !== 1 ? 's' : ''})</span>
                    <span>${pricingDetails.subtotal.toFixed(2)}</span>
                  </div>
                  
                  {pricingDetails.qualifiesForDiscount && (
                    <div className="flex justify-between text-green-600">
                      <span className="flex items-center">
                        <Percent className="h-4 w-4 mr-1" />
                        10% Extended Stay Discount (8+ {pricingDetails.unitLabel}s)
                      </span>
                      <span>-${pricingDetails.stayDiscountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {selectedAddOns.length > 0 && (
                    <>
                      <div className="border-t pt-2 mt-2">
                        <p className="font-medium mb-1">Additional Services:</p>
                        {selectedAddOns.map((addOn, idx) => (
                          <div key={idx} className="flex justify-between text-muted-foreground">
                            <span>• {addOn.name}</span>
                            <span>{addOn.price}{addOn.unit === 'per 24 hours' ? ` × ${pricingDetails.stayCount}` : ''}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between">
                        <span>Add-ons Subtotal</span>
                        <span>${pricingDetails.addOnsTotal.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  
                  {pricingDetails.latePickupCharge > 0 && (
                    <div className="flex justify-between">
                      <span>Late Pickup Daycare Charge</span>
                      <span>${pricingDetails.latePickupCharge.toFixed(2)}</span>
                    </div>
                  )}

                  {/* Promo Code Section */}
                  <div className="border-t pt-4 mt-4" data-testid="promo-code-section">
                    <Label className="text-sm font-medium mb-2 block">Promo Code</Label>
                    {promoApplied ? (
                      <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3" data-testid="promo-applied-banner">
                        <div className="flex items-center text-green-700">
                          <CheckCircle className="h-4 w-4 mr-2" />
                          <span className="font-medium" data-testid="promo-applied-code">{promoCode.toUpperCase()}</span>
                          <span className="ml-2 text-sm">- 10% off applied!</span>
                        </div>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          onClick={removePromoCode}
                          className="text-green-700 hover:text-green-800"
                          data-testid="promo-remove-btn"
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter promo code"
                          value={promoCode}
                          onChange={(e) => {
                            setPromoCode(e.target.value);
                            setPromoError('');
                          }}
                          className={promoError ? 'border-red-500' : ''}
                          data-testid="promo-code-input"
                        />
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={applyPromoCode}
                          disabled={!promoCode.trim()}
                          data-testid="promo-apply-btn"
                        >
                          Apply
                        </Button>
                      </div>
                    )}
                    {promoError && (
                      <p className="text-red-500 text-sm mt-1" data-testid="promo-error-msg">{promoError}</p>
                    )}
                  </div>

                  {/* Promo Discount Line */}
                  {pricingDetails.promoApplied && (
                    <div className="flex justify-between text-green-600 mt-2" data-testid="promo-discount-line">
                      <span className="flex items-center">
                        <Percent className="h-4 w-4 mr-1" />
                        Promo Code Discount (10%)
                      </span>
                      <span data-testid="promo-discount-amount">-${pricingDetails.promoDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="border-t pt-2 mt-2 flex justify-between font-bold text-lg">
                    <span>Estimated Total</span>
                    <span className="text-primary" data-testid="grand-total-amount">${pricingDetails.grandTotal.toFixed(2)}</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  * Final price may vary. Peak season surcharges ($5/day) apply during holidays and school breaks.
                </p>
              </div>

              <div className="flex gap-4 pt-4">
                <Button type="button" variant="outline" onClick={() => setStep(3)} className="flex-1">
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex-1"
                  size="lg"
                >
                  {isSubmitting ? 'Submitting...' : 'Confirm Booking'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Booking;
